/**
 * ChatWidget Component
 * Phase 6.5 - Chat Conversationnel (CFO Virtuel)
 *
 * Floating chat widget for the virtual CFO assistant
 */

import React, { useRef, useEffect } from 'react';
import {
  MessageSquare,
  X,
  Minimize2,
  Maximize2,
  RotateCcw,
  Bot,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useChat } from '../hooks/useChat';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import type { SuggestedAction, ChatResponseSource } from '../types/chat.types';

interface ChatWidgetProps {
  className?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ className = '' }) => {
  const {
    widgetState,
    messages,
    isLoading,
    isSending,
    error,
    toggleWidget,
    closeWidget,
    minimizeWidget,
    maximizeWidget,
    sendMessage,
    startNewConversation,
    submitFeedback,
    clearError,
  } = useChat();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && widgetState.isOpen && !widgetState.isMinimized) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, widgetState.isOpen, widgetState.isMinimized]);

  // Handle suggested action click
  const handleActionClick = (action: SuggestedAction) => {
    // For navigation actions, we could use react-router
    // For now, just send a follow-up message
    if (action.type === 'NAVIGATE' && action.payload.path) {
      window.location.href = action.payload.path;
    } else {
      sendMessage(`${action.label}`);
    }
  };

  // Handle source click
  const handleSourceClick = (source: ChatResponseSource) => {
    // Navigate to the source detail page
    const pathMap: Record<string, string> = {
      invoice: '/invoices',
      client: '/clients',
      quote: '/quotes',
      expense: '/expenses',
      forecast: '/treasury/forecasts',
    };
    const basePath = pathMap[source.type] || '/';
    window.location.href = `${basePath}/${source.id}`;
  };

  // Handle feedback
  const handleFeedback = (messageId: string, rating: number) => {
    submitFeedback(messageId, rating);
  };

  // Render closed state (floating button)
  if (!widgetState.isOpen) {
    return (
      <button
        onClick={toggleWidget}
        className={`fixed bottom-6 right-6 z-50 p-4 rounded-full
          bg-gradient-to-br from-teal-500 to-teal-600 text-white
          shadow-lg shadow-teal-500/30 hover:shadow-xl hover:shadow-teal-500/40
          hover:scale-105 transition-all duration-300 group ${className}`}
        title="Ouvrir le CFO Virtuel"
      >
        <MessageSquare className="w-6 h-6" />
        <span className="absolute -top-1 -right-1 w-3 h-3 bg-lime-400 rounded-full animate-pulse" />
      </button>
    );
  }

  // Render minimized state
  if (widgetState.isMinimized) {
    return (
      <div
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3
          bg-white rounded-full shadow-lg border border-gray-100
          cursor-pointer hover:shadow-xl transition-all duration-300 ${className}`}
        onClick={maximizeWidget}
      >
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <span className="text-sm font-medium text-gray-700">CFO Virtuel</span>
        <Maximize2 className="w-4 h-4 text-gray-400" />
      </div>
    );
  }

  // Render full widget
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 w-[400px] max-h-[600px] flex flex-col
        bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-300/30
        border border-white/50 overflow-hidden animate-fade-in-up ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">CFO Virtuel</h3>
            <p className="text-xs text-teal-100">Assistant financier IA</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={startNewConversation}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Nouvelle conversation"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <button
            onClick={minimizeWidget}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Minimiser"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={closeWidget}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-100 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-xs text-red-600 flex-1">{error}</p>
          <button
            onClick={clearError}
            className="text-red-400 hover:text-red-600"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[400px] bg-gradient-to-b from-gray-50/50 to-white"
      >
        {/* Welcome message if no messages */}
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-100 to-teal-50 flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-teal-600" />
            </div>
            <h4 className="font-semibold text-gray-800 mb-1">
              Bonjour ! Je suis votre CFO Virtuel
            </h4>
            <p className="text-sm text-gray-500 max-w-[280px]">
              Posez-moi vos questions sur votre tresorerie, factures, clients ou
              demandez des recommandations.
            </p>
          </div>
        )}

        {/* Messages list */}
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            onFeedback={(rating) => handleFeedback(message.id, rating)}
            onActionClick={handleActionClick}
            onSourceClick={handleSourceClick}
          />
        ))}

        {/* Loading indicator */}
        {isSending && (
          <div className="flex items-center gap-3 animate-fade-in">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            </div>
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-gray-100 bg-white/80 backdrop-blur">
        <ChatInput
          onSend={sendMessage}
          isLoading={isSending}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default ChatWidget;
